import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle
} from "@mui/material";
import api from "../services/api";
import { Link } from "react-router-dom";

const OTP_TIMER = 120;

/**
 * WelcomePage component is the main page of the application.
 * It allows users to enter their email and start the authentication process.
 *
 * @return {JSX.Element} The WelcomePage component
 */
export default function WelcomePage() {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");

  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: "",
        message: "",
    });

  /**
   * Fetches the token from the URL parameters and validates it.
   * Sets the email, username, and full name of the user from the response.
   * Sets an alert dialog if the token is invalid or expired.
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const t = urlParams.get("token");
    setToken(t);

    api.get(`/validate-token/?token=${t}`)
      .then((res) => {
        setEmail(res.data.email);
        setUsername(res.data.username);
        setFullName(res.data.full_name || "User");
      })
      .catch(() => 
        setAlertDialog({
          open: true,
          title: "Invalid Link",
          message: "Invalid or expired link",
        })
      )
  }, []);

  /**
   * Decreases the timer and sets an error message if the timer is zero and the step is 2.
   * Clears the interval when the timer or step changes.
   * 
   * @return {void}
   */
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    if (timer === 0 && step === 2) {
      setErrorMessage(
        <span style={{color : "red"}}>
          OTP has expired. Please resend OTP.
        </span>
      )
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  /**
   * Sends an OTP to the user's email.
   * Sets the error message and loading state.
   * Makes a POST request to the server to send the OTP.
   * Sets the error message and step if the request is successful.
   * Sets the timer and error message if the request fails.
   * Sets the loading state to false.
   *
   * @return {Promise<void>} A Promise that resolves when the OTP is sent.
   */
  const sendOtp = async () => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await api.post(`/send-otp/`, { token });
      setErrorMessage(
        <span style={{ color: "green" }}>
          OTP has been sent to your email: {email}
        </span>
      );
      setStep(2);
      setTimer(OTP_TIMER);
    } catch {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Failed to send OTP
        </span>
      );
    }
    setLoading(false);
  };

  /**
   * Resends an OTP to the user's email if the timer is zero.
   * Sets the error message and loading state.
   * Makes a POST request to the server to send the OTP.
   * Sets the timer and error message if the request is successful.
   * Sets the error message if the timer is not zero.
   * Sets the loading state to false.
   *
   * @return {Promise<void>} A Promise that resolves when the OTP is resent.
   */
  const resendOtp = async () => {
    setErrorMessage(null);
    if (timer > 0) {
      setErrorMessage(
        <span style={{ color: "red" }}>
          OTP has not expired yet. Please wait.
        </span>
      );
      return;
    }

    setLoading(true);
    try {
      await api.post(`/send-otp/`, { token });
      setTimer(OTP_TIMER);
    } catch {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Failed to resend OTP
        </span>
      );
    }
    setLoading(false);
  };

  /**
   * Verifies the OTP entered by the user.
   * Sets the error message and loading state.
   * Makes a POST request to the server to verify the OTP.
   * Sets the error message and step if the request is successful.
   * Sets the error message if the request fails.
   * Sets the loading state to false.
   *
   * @return {Promise<void>} A Promise that resolves when the OTP is verified.
   */
  const verifyOtp = async () => {
    setErrorMessage(null);
    setLoading(true);
    try {
      await api.post(`/verify-otp/`, { token, otp });
      setErrorMessage(
        <span style={{ color: "green" }}>
          OTP verified successfully
        </span>
      );
      setStep(3);
    } catch {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Invalid OTP
        </span>
      );
    }
    setLoading(false);
  };

  /**
   * Checks if the password entered by the user is valid.
   *
   * @return {boolean} Returns true if the password is valid, false otherwise.
   */
  const isPasswordValid = () => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#,.)('~`_+\[=}{;:\-]).{8,}$/;
    return regex.test(password);
  };

  /**
   * Sets the user's password.
   * Sets the error message and loading state.
   * Checks if the password is valid.
   * Sets the error message if the password is not valid.
   * Checks if the password and confirm password match.
   * Sets the error message if the passwords do not match.
   * Makes a POST request to the server to set the password.
   * Sets the error message and redirects to the UI link if the request is successful.
   * Sets the error message if the request fails.
   * Sets the loading state to false.
   *
   * @return {Promise<void>} A Promise that resolves when the password is set.
   */
  const setPasswordHandler = async () => {
    setErrorMessage(null);

    if (password && !isPasswordValid()) {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Password must be 8+ chars, include uppercase, number & special character
        </span>
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Passwords do not match
        </span>
      );
      return;
    }

    setLoading(true);
    try {
      await api.post(`/set-password/`, { token, password });
      setErrorMessage(
        <span style={{ color: "green" }}>
          Password set successfully!
        </span>
      );
      window.location.href = `${api.ui_link}`;
    } catch {
      setErrorMessage(
        <span style={{ color: "red" }}>
          Failed to set password
        </span>
      );
    }
    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={{ color: "#4a3aff", marginBottom: 5 }}>
            Welcome to Talchi AI
        </h2>

        <p style={{ fontSize: 14, color: "#666", marginBottom: 10 }}>
            Secure your account by setting your new password
        </p>

        <h3 style={{ margin: 0 }}>Hello, {fullName} </h3>

        <div style={styles.infoBox}>
          <p><b>Your User ID:</b> {username}</p>
        </div>

        {step === 1 && (
          <>
            <p style={styles.text}>
              Click below to receive a One-Time Password (OTP) on your registered email.
            </p>

            <div style={{ width: "100%", textAlign: "left" }}>
                {errorMessage}
            </div>

            <button
                onClick={sendOtp}
                style={styles.btn}
                disabled={loading || !token}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <input
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              style={styles.inputotp}
            />

            <div style={{ width: "100%", textAlign: "left"}}>
                {errorMessage}
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: 10 }}>
            <button
                onClick={verifyOtp}
                style={{
                  ...styles.btn,
                  opacity: otp.length < 6 || loading ? 0.6 : 1,
                  cursor: otp.length < 6 || loading ? "not-allowed" : "pointer"
                }}
                disabled={otp.length < 6 || loading}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

            {timer > 0 ? (
              <p style={styles.timer}>Resend OTP in {timer}s</p>
            ) : (
              <button
                onClick={resendOtp}
                style={{
                  ...styles.btn,
                  opacity: timer > 0 || loading ? 0.6 : 1,
                  cursor: timer > 0 || loading ? "not-allowed" : "pointer"
                }}
                disabled={timer > 0 || loading}
              >
                Resend OTP
              </button>
            )}
            </div>
          </>
        )}

        {step === 3 && (
          <>

            <p style={{ textAlign: "left", fontSize: 14, marginBottom: 10, fontWeight: 600}}>
              Please set your new password
            </p>

            <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="New Password *"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
            />

            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                fontSize: 12,
                color: "#4a3aff"
              }}
            >
              {showPassword ? "Hide" : "Show"}
            </span>
          </div>

            <div style={{ position: "relative" }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password *"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.input}
              />

              <span
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  fontSize: 12,
                  color: "#4a3aff"
                }}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </span>
            </div>

            <div style={{ width: "100%", textAlign: "left" }}>
                {errorMessage}
            </div>

            <button
              onClick={setPasswordHandler}
              style={{
                ...styles.btn,
                opacity:
                  !password ||
                  !confirmPassword ||
                  loading
                    ? 0.6
                    : 1,
                cursor:
                  !password ||
                  !confirmPassword ||
                  loading
                    ? "not-allowed"
                    : "pointer"
              }}
              disabled={
                !password ||
                !confirmPassword ||
                loading
              }
            >
              {loading ? "Saving..." : "Set Password"}
            </button>
          </>
        )}
      </div>
      <Dialog
          open={alertDialog.open}
          onClose={() => setAlertDialog({ ...alertDialog, open: false })}
      >
          <DialogTitle sx={{ fontWeight: 700 }}>
              {alertDialog.title}
          </DialogTitle>
          <DialogContent>
              <Typography fontSize="14px">
                  {alertDialog.message}
              </Typography>
          </DialogContent>
          <DialogActions>
            <Link to={api.ui_link} style={{ textDecoration: "none" }}>
              <Button variant="contained">
                OK
              </Button>
            </Link>
          </DialogActions>
      </Dialog>
    </div>
  );
}

const styles = {
  page: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f5f7fb",
  },
  card: {
    width: 500,
    padding: 30,
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 5px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
  },
  infoBox: {
    background: "#f1f3f6",
    padding: 10,
    borderRadius: 8,
    margin: "15px 0",
    textAlign: "left",
  },
  input: {
    width: "100%",
    padding: "10px",
    margin: "10px 0",
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  inputotp: {
    width: "100%",
    padding: "10px",
    margin: "10px 0",
    borderRadius: 6,
    border: "1px solid #ccc",
  },
  btn: {
    display: "block",
    width: "fit-content",
    gap: "10px",
    padding: 10,
    background: "#4a3aff",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    marginTop: 10,
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#4a3aff",
    cursor: "pointer",
    marginTop: 10,
  },
  timer: {
    textAlign: "left",
    marginTop: 10,
    color: "#888",
  },
  text: {
    textAlign: "left",
    fontSize: 14,
    color: "#555",
    marginBottom: 10,
  },

  successText: {
    textAlign: "left",
    color: "green",
    fontSize: 14,
    marginBottom: 10,
  },
};